import React from 'react';
import {
  Box, IconButton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { EmptyState, LoadingOverlay, StatusChip } from 'dashboard/components/common';
import { fmtDate } from 'utilities/format';

const STATUS_COLORS = { active: 'success', paused: 'warning', archived: 'default' };

const fmtValue = (d) => {
  if (!d) return '';
  if (d.kind === 'percent') return `${(Number(d.value || 0) / 100).toFixed(2)}%`;
  return `${Number(d.value || 0).toFixed(2)} ${d.currency || ''}`.trim();
};

/**
 * Discount codes table. Each row shows code + description, value (with
 * min-subtotal hint), status chip, usage (with last-used date if any),
 * revenue + total discount given, validity window, plus edit/delete.
 *
 * Usage data is joined client-side via `usageById` so the list keeps
 * working when the usage report endpoint is slow or down.
 */
export default function DiscountsTable({ rows, loading, usageById, onEdit, onDelete }) {
  return (
    <LoadingOverlay loading={loading && rows.length === 0} minHeight={200}>
      <TableContainer
        component={Box}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflowX: 'auto' }}
      >
        <Table size='medium'>
          <TableHead sx={{ bgcolor: 'action.hover' }}>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Used</TableCell>
              <TableCell>Revenue</TableCell>
              <TableCell>Window</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7}><EmptyState>Loading…</EmptyState></TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7}><EmptyState>No discount codes yet.</EmptyState></TableCell></TableRow>
            )}
            {rows.map((d) => (
              <TableRow key={d._id}>
                <TableCell>
                  <Typography fontWeight='700'>{d.code}</Typography>
                  {d.description && (
                    <Typography variant='caption' color='text.secondary' display='block' mt='2px'>
                      {d.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>{fmtValue(d)}</Typography>
                  {Number(d.min_subtotal) > 0 && (
                    <Typography variant='caption' color='text.secondary'>
                      min {d.min_subtotal} {d.currency}
                    </Typography>
                  )}
                </TableCell>
                <TableCell><StatusChip status={d.status} colors={STATUS_COLORS} /></TableCell>
                <TableCell>
                  <Typography variant='body2'>
                    {d.used_count || 0}{d.max_uses ? ` / ${d.max_uses}` : ''}
                  </Typography>
                  {usageById[d._id]?.last_used && (
                    <Typography variant='caption' color='text.secondary'>
                      last {fmtDate(usageById[d._id].last_used)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>
                    {(usageById[d._id]?.revenue || 0).toFixed(2)} {d.currency}
                  </Typography>
                  {Number(usageById[d._id]?.discount_total) > 0 && (
                    <Typography variant='caption' color='error.main'>
                      -{Number(usageById[d._id].discount_total).toFixed(2)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant='caption' color='text.secondary'>
                    {fmtDate(d.starts_at)} → {fmtDate(d.ends_at)}
                  </Typography>
                </TableCell>
                <TableCell align='right'>
                  <Stack direction='row' spacing='4px' justifyContent='flex-end'>
                    <Tooltip title='Edit'>
                      <IconButton size='small' onClick={() => onEdit(d)}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete'>
                      <IconButton size='small' color='error' onClick={() => onDelete(d)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </LoadingOverlay>
  );
}
